/**
 * Users, for the tenant Front Desk (Logic's contract, 9 Oct 2026: specs/front-desk-tenant-backend.md, PR-B and
 * the contract). The server enforces every rule; these calls only carry what it asks for.
 * - create a resident: POST /sam/users (no password: Cognito sends the invitation);
 * - basic data: PUT /sam/users/{username} with name and last_name, and/or photo_url and profile_picture TOGETHER (Logic #675);
 *   any other field is a 400, and the phone goes alone (below);
 * - phone: PUT /sam/users/{username} leaves a PENDING number that the user confirms by SMS at the next login;
 * - history: GET /crud/audit_log, who changed what and when.
 */
import { defaultInstance, directApiInstance } from './httpClient';

// ---- create a resident -------------------------------------------------------------------------------------

export interface CreateResidentRequest {
  email: string;
  name: string;
  family_name: string;
  phone_number?: string;
  /** A residential user type of the tenant (owner, resident, property manager). */
  user_type_id: number;
  /** Required for owner, resident and property manager. */
  estate_id: number | string;
}

export interface CreateResidentResponse {
  user: { username: string; tenant_id: string; created: boolean | string };
  user_id: number | string;
  user_estate_id: number | string;
}

export type CreateResidentFailure = 'email_exists' | 'invalid' | 'forbidden' | 'unknown';

/** What a failed create means for the person: 409 the email exists, 400 a field or the type, 403 not allowed. */
export const createResidentFailure = (err: unknown): CreateResidentFailure => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 409) return 'email_exists';
  if (status === 400) return 'invalid';
  if (status === 403) return 'forbidden';
  return 'unknown';
};

export const createResident = async (body: CreateResidentRequest): Promise<CreateResidentResponse> =>
  directApiInstance<CreateResidentResponse>({ url: '/sam/users', method: 'POST', data: body }) as unknown as Promise<CreateResidentResponse>;

// ---- basic data ---------------------------------------------------------------------------------------------

export interface UserBasics {
  name?: string;
  last_name?: string;
  /** The uploaded photo key (`{ENV}/…`). Goes with `profile_picture`, never alone. */
  photo_url?: string;
  /** The cropped face of that photo (`cropped_faces/{ENV}/…`). Goes with `photo_url`, never alone. */
  profile_picture?: string;
}

/**
 * Only these fields are sent, whatever the caller holds: the server answers 400 to any other. The photo and its
 * cropped face travel as a pair; one without the other is dropped, so a half photo is never sent.
 */
export const basicsPayload = (basics: UserBasics): UserBasics => {
  const out: UserBasics = {};
  if (basics.name !== undefined) out.name = basics.name.trim();
  if (basics.last_name !== undefined) out.last_name = basics.last_name.trim();
  if (basics.photo_url && basics.profile_picture) {
    out.photo_url = basics.photo_url;
    out.profile_picture = basics.profile_picture;
  }
  return out;
};

export type BasicsFailure = 'NO_ACCOUNT' | 'REFUSED' | 'DAILY_LIMIT' | 'UNKNOWN';

export class BasicsError extends Error {
  constructor(public readonly code: BasicsFailure) {
    super(code);
    this.name = 'BasicsError';
  }
}

/**
 * `PUT /sam/users/{username}` with the user's EMAIL as the username (Cognito Username or email, Logic #675): 404 is
 * no sign-in account, 400 a field the route does not take, 429 the Front Desk's daily limit of photo changes (10 a
 * day; the status is Logic's to confirm).
 */
export const updateUserBasics = async (email: string, basics: UserBasics) => {
  try {
    return await directApiInstance({
      url: `/sam/users/${encodeURIComponent(email.trim())}`,
      method: 'PUT',
      data: basicsPayload(basics),
    });
  } catch (err: any) {
    if (err?.response?.status === 404) throw new BasicsError('NO_ACCOUNT');
    if (err?.response?.status === 400) throw new BasicsError('REFUSED');
    if (err?.response?.status === 429) throw new BasicsError('DAILY_LIMIT');
    throw err;
  }
};

// ---- phone (pending, confirmed by the user) -----------------------------------------------------------------

export type PhoneChangeFailure = 'INVALID_PHONE' | 'PHONE_IN_USE' | 'NO_ACCOUNT' | 'UNKNOWN';

export class PhoneChangeError extends Error {
  constructor(public readonly code: PhoneChangeFailure) {
    super(code);
    this.name = 'PhoneChangeError';
  }
}

export interface PhoneChangeResult {
  status: 'PENDING_CONFIRMATION';
  /** Masked: «+1809***1234». */
  pendingPhone: string;
}

/**
 * `PUT /sam/users/{username}` with the user's EMAIL as the username (Logic, #634): the server resolves it to the one
 * account of the pool. No account, several, another tenant, or an email with quotes or spaces: 404, which is
 * «NO_ACCOUNT» here.
 */
export const requestPhoneChange = async (email: string, phoneE164: string): Promise<PhoneChangeResult> => {
  try {
    const res = (await directApiInstance<{ status: 'PENDING_CONFIRMATION'; pending_phone: string }>({
      url: `/sam/users/${encodeURIComponent(email.trim())}`,
      method: 'PUT',
      data: { phone_number: phoneE164 },
    })) as unknown as { status: 'PENDING_CONFIRMATION'; pending_phone: string };
    return { status: res.status, pendingPhone: res.pending_phone };
  } catch (err: any) {
    const code = err?.response?.data?.code;
    if (err?.response?.status === 404) throw new PhoneChangeError('NO_ACCOUNT');
    if (err?.response?.status === 409 && code === 'PHONE_IN_USE') throw new PhoneChangeError('PHONE_IN_USE');
    if (err?.response?.status === 400 && code === 'INVALID_PHONE') throw new PhoneChangeError('INVALID_PHONE');
    throw err;
  }
};

// ---- history ------------------------------------------------------------------------------------------------

export interface AuditChange { field: string; old: unknown; new: unknown }

export interface AuditLogRow {
  id: number | string;
  created_at: string;
  author_user_id: number | string | null;
  author_name: string | null;
  operation: string;
  changes: AuditChange[];
}

/** The server pages the history at 50 rows at most. */
export const AUDIT_LOG_MAX_LIMIT = 50;

export const getUserAuditLog = async (userId: number | string, opts: { page?: number; limit?: number } = {}): Promise<AuditLogRow[]> => {
  const res = (await defaultInstance<{ data?: AuditLogRow[] } | AuditLogRow[]>({
    url: '/audit_log',
    method: 'GET',
    params: { table_name: 'user', record_id: userId, page: opts.page ?? 1, limit: Math.min(opts.limit ?? 20, AUDIT_LOG_MAX_LIMIT) },
  })) as unknown as { data?: AuditLogRow[] } | AuditLogRow[];
  return Array.isArray(res) ? res : res?.data ?? [];
};
